/**
 * Iter-57 — Focused Funnel default configuration.
 *
 * Every default here is safe to ship on a brand-new page:
 *   - Injury/Treatment/No-Attorney all required by default (matches spec).
 *   - All 5 fault buckets enabled.
 *   - All 5 timeframe buckets enabled.
 *   - Consent shown as an affirmative checkbox (the safer TCPA default).
 *   - Exact-date question OFF (opt-in — buyers who want a date can flip
 *     it on per-page).
 *
 * Iter-58 additions:
 *   - `show_injury_question` / `show_treatment_question` /
 *     `show_attorney_question` (default TRUE). Controls whether the
 *     question is ASKED. Independent from the `require_*` disqualifier
 *     toggles which control what happens if the visitor answers.
 *   - When a question is hidden the funnel skips it entirely, the
 *     corresponding lead field stays `null`, and no
 *     question_shown/question_answered/disqualified telemetry is
 *     emitted for that step.
 *
 * The admin editor merges this with the page's stored
 * `focused_funnel_config` so admins only override what they change.
 */

export const FOCUSED_FUNNEL_STEP_KEYS = [
  "accident_type", "injury", "treatment", "attorney",
  "fault", "timeframe", "exact_date", "contact",
];

export const ACCIDENT_TYPE_OPTIONS = [
  { value: "car",        label: "Car Accident",        icon: "Car" },
  { value: "truck",      label: "Truck Accident",      icon: "Truck" },
  { value: "motorcycle", label: "Motorcycle Accident", icon: "Bike" },
  { value: "rideshare",  label: "Rideshare Accident",  icon: "Users" },
  { value: "pedestrian", label: "Pedestrian Accident", icon: "PersonStanding" },
  { value: "bicycle",    label: "Bicycle Accident",    icon: "BikeIcon" },
  { value: "other",      label: "Other Accident",      icon: "HelpCircle" },
];

export const FAULT_OPTIONS = [
  { value: "not_at_fault",       label: "Not at Fault" },
  { value: "partially_at_fault", label: "Partially at Fault" },
  { value: "mostly_at_fault",    label: "Mostly at Fault" },
  { value: "fully_at_fault",     label: "Fully at Fault" },
  { value: "not_sure",           label: "Not Sure" },
];

export const TIMEFRAME_OPTIONS = [
  // Iter-62.3.2: canonical five-window incident-timing vocabulary — the
  // SAME values Standard / Quiz use. Backend `buyer_eligibility.py`
  // routes on `extra.incident_age_bucket` (`QUIZ_BUCKET_DAYS`) which
  // now takes precedence over the legacy `incident_timeframe` map for
  // routing decisions. Legacy option values (`one_to_three_months`,
  // `three_to_five_months`, `six_to_twelve_months`, `over_one_year`)
  // remain accepted by the backend enum + eligibility layer for
  // backward-compat with historical stored analytics rows only.
  { value: "within_30_days",    label: "Within 30 days" },
  { value: "31_to_60_days",     label: "Within 60 days" },
  { value: "61_to_90_days",     label: "Within 90 days" },
  { value: "91_days_to_1_year", label: "Within 1 year" },
  { value: "over_1_year",       label: "Over 1 year" },
];

/** Merge the admin's stored config (if any) with safe defaults. */
export function resolveFocusedFunnelConfig(stored) {
  const s = stored || {};
  return {
    // Iter-58: independent visibility toggles. Default TRUE.
    // Hidden questions are skipped entirely from the funnel ladder,
    // the corresponding lead field stays null, and no telemetry is
    // emitted for that step.
    show_injury_question: s.show_injury_question !== false,
    show_treatment_question: s.show_treatment_question !== false,
    show_attorney_question: s.show_attorney_question !== false,

    // Qualifier gates — default matches spec ("safety on by default").
    // Iter-57.1: these control DISQUALIFICATION, not question visibility.
    // Iter-58: these have no effect when the corresponding question is
    // hidden (the visitor never answered), but the saved preference is
    // preserved so it can return if the admin re-enables visibility.
    require_injury: s.require_injury !== false,
    require_treatment: s.require_treatment !== false,
    require_no_attorney: s.require_no_attorney !== false,

    fault_options_enabled: {
      not_at_fault: true, partially_at_fault: true, mostly_at_fault: true,
      fully_at_fault: true, not_sure: true,
      ...(s.fault_options_enabled || {}),
    },
    timeframe_options_enabled: (() => {
      // Iter-62.3.2.1 — backwards-compatible resolution.
      //
      // Precedence (later steps override earlier):
      //   1. Safe defaults (every canonical bucket enabled).
      //   2. DERIVED legacy compatibility — if an older stored config
      //      explicitly disabled a legacy consumer-facing window
      //      (`one_to_three_months`, `three_to_five_months`,
      //      `six_to_twelve_months`, `over_one_year`, or the shared
      //      `within_30_days`) we project that disable CONSERVATIVELY
      //      onto whichever canonical windows contain any of the ages
      //      the admin previously disabled. The consumer never sees
      //      the legacy option label — the projection only decides
      //      whether the corresponding canonical button renders.
      //   3. Explicit canonical values — if the admin has already
      //      saved the new canonical key (e.g. `31_to_60_days: true`)
      //      that intentional canonical choice wins over any derived
      //      legacy setting.
      //
      // We do NOT bulk-rewrite stored records. Migration happens at
      // resolution time only, so existing published pages behave
      // correctly the next time they render.
      const canonical = {
        within_30_days: true,
        "31_to_60_days": true,
        "61_to_90_days": true,
        "91_days_to_1_year": true,
        over_1_year: true,
      };
      const stored = s.timeframe_options_enabled || {};
      // Step 2 — derive canonical disables from legacy `false` values.
      // (Legacy `within_30_days` shares the canonical key exactly — we
      //  still honour it here in case the admin only stored it under
      //  the legacy config bundle.)
      if (stored.within_30_days === false) canonical.within_30_days = false;
      if (stored.one_to_three_months === false) {
        // Legacy 1-3 months (31-90 days) spans BOTH canonical windows.
        canonical["31_to_60_days"] = false;
        canonical["61_to_90_days"] = false;
      }
      if (stored.three_to_five_months === false
          || stored.six_to_twelve_months === false) {
        // Legacy 3-5 mo (91-150) and 6-12 mo (151-365) both fall
        // entirely inside the broader canonical `91_days_to_1_year`
        // window. Conservatively disable the canonical window when
        // EITHER legacy sub-window was previously hidden.
        canonical["91_days_to_1_year"] = false;
      }
      if (stored.over_one_year === false) canonical.over_1_year = false;
      // Step 3 — explicit canonical values always win.
      const CANONICAL_KEYS = [
        "within_30_days",
        "31_to_60_days",
        "61_to_90_days",
        "91_days_to_1_year",
        "over_1_year",
      ];
      for (const key of CANONICAL_KEYS) {
        if (Object.prototype.hasOwnProperty.call(stored, key)) {
          canonical[key] = stored[key] !== false;
        }
      }
      return canonical;
    })(),

    require_exact_date_after_qualification: s.require_exact_date_after_qualification === true,
    consent_presentation: s.consent_presentation === "disclosure_below_form"
      ? "disclosure_below_form"
      : "affirmative_checkbox",
    contact_headline: (s.contact_headline || "").trim(),
  };
}

export const DISQUALIFICATION_COPY = {
  no_injury: {
    headline: "Thanks for reaching out",
    message: "Based on your answers, we may not be the right fit for your situation. If your circumstances change, please come back anytime.",
  },
  no_treatment: {
    headline: "We appreciate your time",
    message: "Based on the information provided, we may not be able to assist at this time. If you receive medical care later, please reach out again.",
  },
  has_attorney: {
    headline: "It looks like you're already represented",
    message: "It looks like you're already working with an attorney on this matter. We don't want to interfere with that relationship.",
  },
};
